/**
 * Generating rough top down 2D outlines for design material SDF cells.
 * @module
 */
import { intersectRay2Ray2 } from "geom/intersect";
import { polyLine2Bevel, polyLine2MakeAxisAlignedRect, polyLine2MakeRoundedRect } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { ray2New } from "geom/ray2";
import type { SdfCellInfo } from "../designMaterialTypes";

export function clampCutXfromYForGeometry(
  cell: Pick<SdfCellInfo, "cutXfromY" | "cutXfromYScale" | "xSize" | "ySize" | "roundStrength">,
): number {
  if (!cell.cutXfromY) {
    return 0;
  }
  return Math.min(cell.cutXfromYScale, (cell.xSize * 0.5 - cell.roundStrength) / (cell.ySize * 0.5));
}

/**
 * @returns The modified x coordinate.
 */
export function cutXFromYForGeometry(x: number, y: number, cutXfromYScale: number): number {
  return x - Math.sign(x) * y * cutXfromYScale;
}

/**
 * @returns How far from the cell edge the rounded segments of the cell outline intersect, making the cell's silhouette smaller.
 */
function clipRoundingCircles(width: number, roundingRadius: number) {
  if (width * 0.5 < roundingRadius) {
    const intersectionXFromCircleCenter = roundingRadius - width * 0.5;
    const intersectionYFromCircleCenter = Math.sqrt(
      roundingRadius * roundingRadius - intersectionXFromCircleCenter * intersectionXFromCircleCenter,
    );
    return roundingRadius - intersectionYFromCircleCenter;
  }
  return 0;
}

/**
 * Generates a polyline describing the outline of an SDF cell. Ignores rotation.
 */
export function generateCellOutline(cell: SdfCellInfo, maxStepDegrees: number = 10): PolyLine2 | null {
  const {
    roundStrength,
    xSize,
    ySize,
    zSize,
    useBevels,
    bevelDistanceX,
    bevelDistanceY,
    bevelDistanceZ,
    roundSidesMode,
    cutXfromY,
    cutXfromYScale,
  } = cell;
  if (xSize === 0 || ySize === 0 || zSize === 0) {
    return null;
  }
  let loop: PolyLine2 | undefined;
  let genXHalfWidth = xSize * 0.5;
  let genYHalfWidth = ySize * 0.5;

  let bevelCutX, bevelCutY;
  if (useBevels) {
    if (bevelDistanceX > 0 && bevelDistanceX * bevelDistanceX * 2 > Math.pow(zSize * 0.5, 2)) {
      const intersection = intersectRay2Ray2(
        ySize * 0.5 - Math.SQRT2 * bevelDistanceX,
        -zSize * 0.5,
        Math.SQRT1_2,
        Math.SQRT1_2,
        0,
        0,
        1,
        0,
      );
      if (!intersection) {
        // Only checked to satisfy strict null checks, rays above have hardcoded different direction so they'll always intersect.
        return null;
      }
      const { x: intersectionY } = intersection;
      if (intersectionY < 0) {
        return null;
      }
      bevelCutY = intersectionY;
    }
    if (bevelDistanceY > 0 && bevelDistanceY * bevelDistanceY * 2 > Math.pow(zSize * 0.5, 2)) {
      const intersection = intersectRay2Ray2(
        xSize * 0.5 - Math.SQRT2 * bevelDistanceY,
        -zSize * 0.5,
        Math.SQRT1_2,
        Math.SQRT1_2,
        0,
        0,
        1,
        0,
      );
      if (!intersection) {
        // Only checked to satisfy strict null checks, rays above have hardcoded different direction so they'll always intersect.
        return null;
      }
      const { x: intersectionX } = intersection;
      if (intersectionX < 0) {
        return null;
      }
      bevelCutX = intersectionX;
    }
  }
  // Compute the effect that aggressive x/y rounding has on the size of the rectangle.
  if (roundSidesMode === "roundX") {
    const offset = clipRoundingCircles(zSize, roundStrength);
    if (offset > ySize * 0.5) {
      return null;
    }
    genYHalfWidth = genYHalfWidth - offset;
  } else if (roundSidesMode === "roundY") {
    const offset = clipRoundingCircles(zSize, roundStrength);
    if (offset > xSize * 0.5) {
      return null;
    }
    genXHalfWidth = genXHalfWidth - offset;
  }

  if (roundSidesMode === "roundSides" || roundSidesMode === "roundZ") {
    loop = polyLine2MakeRoundedRect(
      genXHalfWidth,
      -genYHalfWidth,
      -genXHalfWidth,
      genYHalfWidth,
      roundStrength,
      maxStepDegrees,
    );
    if (loop === undefined) {
      return null;
    }
    if (roundSidesMode === "roundSides" && cutXfromY && cutXfromYScale > 0) {
      const clampedCutXfromYScale = clampCutXfromYForGeometry(cell);
      for (let i = 0, l = loop.length; i < l; i += 2) {
        loop[i] = cutXFromYForGeometry(loop[i], loop[i + 1], clampedCutXfromYScale);
      }
    }
  } else {
    // The order of the negative and positive arguments here is important to set the winding direction.
    loop = polyLine2MakeAxisAlignedRect(genXHalfWidth, -genYHalfWidth, -genXHalfWidth, genYHalfWidth);
    if (roundSidesMode === "none" && cutXfromY && cutXfromYScale > 0) {
      for (let i = 0, l = loop.length; i < l; i += 2) {
        loop[i] = cutXFromYForGeometry(loop[i], loop[i + 1], cutXfromYScale);
      }
    }
  }
  if (bevelCutX !== undefined) {
    loop = polyLine2Bevel(loop, ray2New(-bevelCutX, 0, 0, 1));
    loop = polyLine2Bevel(loop, ray2New(bevelCutX, 0, 0, -1));
  }
  if (bevelCutY !== undefined) {
    loop = polyLine2Bevel(loop, ray2New(0, -bevelCutY, -1, 0));
    loop = polyLine2Bevel(loop, ray2New(0, bevelCutY, 1, 0));
  }
  if (useBevels && bevelDistanceZ > 0) {
    const bevelRay = ray2New(-xSize * 0.5 + Math.SQRT2 * bevelDistanceZ, -ySize * 0.5, -Math.SQRT1_2, Math.SQRT1_2);
    loop = polyLine2Bevel(loop, bevelRay);
    bevelRay.originY = ySize * 0.5;
    bevelRay.dirX = Math.SQRT1_2;
    loop = polyLine2Bevel(loop, bevelRay);

    bevelRay.originX = xSize * 0.5 - Math.SQRT2 * bevelDistanceZ;
    bevelRay.dirY = -Math.SQRT1_2;
    loop = polyLine2Bevel(loop, bevelRay);

    bevelRay.originY = -ySize * 0.5;
    bevelRay.dirX = -Math.SQRT1_2;
    loop = polyLine2Bevel(loop, bevelRay);
  }
  return loop;
}
